// Pure helpers for the Cold Chain page (spec §9). Mirrors server/src/coldchain/overview-rules.ts ordering.
export const STATUS_RANK = { excursion: 0, no_data: 1, pending: 2, unknown: 3, ok: 4 };
export const BATTERY_LOW_V = 2.7;

export function sortPoints(points) {
  const rank = (p) => (p.enabled ? STATUS_RANK[p.status] ?? 3 : 5);
  return [...points].sort((a, b) => rank(a) - rank(b) || (a.deviceName ?? '').localeCompare(b.deviceName ?? '') || a.slot - b.slot);
}

/**
 * Vehicles as one list; sites one group each (slice C spec §6), in the order their first, most urgent, row appears.
 * A row is grouped by its site; a site-mode row with no site (left by a rollback, before the boot resync) falls back
 * to its place. Each group carries the site status: the worst of its enabled cold points, 'off' when none is enabled.
 */
export function groupOverview(points) {
  const sorted = sortPoints(points ?? []);
  const vehicles = sorted.filter((p) => p.mode !== 'site');
  const groups = new Map();
  for (const p of sorted) {
    if (p.mode !== 'site') continue;
    const key = p.siteId ? `site:${p.siteId}` : `poi:${p.poiId ?? ''}`;
    if (!groups.has(key)) {
      groups.set(key, { key, siteId: p.siteId ?? null, siteName: p.siteName ?? null, poiId: p.poiId ?? null, poiName: p.poiName ?? null, points: [] });
    }
    groups.get(key).points.push(p);
  }
  const sites = [...groups.values()].map((g) => ({ ...g, status: siteStatus(g.points) }));
  return { vehicles, sites };
}

/** The worst status among a site's enabled cold points (overview order); 'off' when none is enabled (Decision 11). */
export function siteStatus(points) {
  return summarizeColdPoints(points)?.status ?? 'off';
}

/**
 * One summary for a device's cold points (Live Map list chip): enabled points only, the worst
 * status wins (same order as the overview), and the shown value is the worst point's own reading.
 * Returns null when the device has no enabled cold points.
 */
export function summarizeColdPoints(points) {
  const live = (points ?? []).filter((p) => p.enabled);
  if (live.length === 0) return null;
  const rank = (p) => STATUS_RANK[p.status] ?? 3;
  const worst = live.reduce((a, b) => (rank(b) < rank(a) ? b : a));
  const status = worst.status;
  return { status, value: status === 'no_data' || typeof worst.value !== 'number' ? null : worst.value, points: live };
}

export function statusTone(status) {
  return { ok: 'green', pending: 'amber', excursion: 'red' }[status] ?? 'grey';
}

export function formatTemp(v) {
  return typeof v === 'number' ? `${v.toFixed(1)} °C` : '—';
}

export function formatRange(min, max) {
  if (typeof min === 'number' && typeof max === 'number') return `${min}–${max} °C`;
  if (typeof max === 'number') return `≤ ${max} °C`;
  if (typeof min === 'number') return `≥ ${min} °C`;
  return '—';
}

export function minutesSince(iso, nowMs) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.max(0, Math.floor((nowMs - t) / 60_000)) : null;
}

export function isBatteryLow(v) {
  return typeof v === 'number' && v < BATTERY_LOW_V;
}

/**
 * Render a translated sentence with its numbers isolated in <bdi> (so they keep their digits and
 * direction inside RTL text). `translate(tokens)` is `(o) => t(key, default, o)`; each value in
 * `values` is swapped for a sentinel, then rebuilt through `wrap(value, key)`.
 * Returns an array of strings and wrapped nodes.
 */
export function splitNumbers(translate, values, wrap) {
  const tokens = Object.fromEntries(Object.keys(values).map((k) => [k, `@@${k}@@`]));
  return String(translate(tokens)).split(/(@@\w+@@)/).filter(Boolean).map((part) => {
    const m = /^@@(\w+)@@$/.exec(part);
    return m ? wrap(values[m[1]], m[1]) : part;
  });
}
